import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { URDFJoint, URDFRobot } from '@rocketfarm/urdf-loader';
import {
  BehaviorSubject,
  Observable,
  ReplaySubject,
  Subscription,
  combineLatest,
  iif,
  of,
} from 'rxjs';
import {
  filter,
  map,
  shareReplay,
  skipWhile,
  startWith,
  switchMap,
  take,
  takeUntil,
} from 'rxjs/operators';
import * as THREE from 'three';
import { MathUtils } from 'three';
import { toRequestState } from '../../../../../data-request/operators';
import { InstancedDynamicBox } from '../../../../../models_new/classes/3dview/dynamic-box';
import { NewCollsionIndicator } from '../../../../../models_new/classes/3dview/timed-objects/new-collision-indicator';
import { Transform } from '../../../../../models_new/classes/3dview/transform';
import { settings } from '../../../../../models_new/config/application-settings';
import { defaultRobotPose } from '../../../../../models_new/config/default-robot-pose';
import { LabelOrientation } from '../../../../../models_new/enums/label-orientation';
import { ReadResult } from '../../../../../models_new/types/rosbag/rosbag-types';
import { ISimulationMovementBox } from '../../../../../models_new/types/simulation-details';
import { NewAssetStoreService } from '../../../../../services/3dview/asset-store.service';
import { RenderingService } from '../../../../../services/3dview/rendering.service';
import { ThreeExporterService } from '../../../../../services/3dview/three-exporter.service';
import { NotificationService } from '../../../../../services/notification.service';
import {
  RosbagPlayerEventType,
  RosbagPlayerService,
} from '../../../../../services/rosbag/rosbag-player.service';
import { ThreeUtils } from '../../../../../utils/three-utils';
import { URDFUtils } from '../../../../../utils/urdf-utils';
import { New_ThreeViewComponent } from '../../three-view/three-view.component';

@Component({
  selector: 'app-simulation-view',
  // Doesn't need a custom template.
  templateUrl: '../../three-view/three-view.component.html',
  styleUrls: [
    '../../three-view/three-view.component.scss',
    './simulation-view.component.scss',
  ],
})
export class SimulationViewComponent
  extends New_ThreeViewComponent
  implements OnInit, OnChanges, AfterViewInit, OnDestroy
{
  /**
   * ID of the simulation, used for **caching** of assets between navigations.
   */
  @Input() id = 'default';

  /**
   * URL of the urdf for the simulation.
   */
  @Input() urdfURL: string;
  @Input() labelOrientation: LabelOrientation;
  @Input() customerOrgWallSticker: THREE.Texture;
  @Input() salesOrgWallSticker: THREE.Texture;
  @Input() boxSticker: THREE.Texture;
  @Input() productModelId$: BehaviorSubject<string> =
    new BehaviorSubject<string>(null);

  // The min and max dimensions the robot moved in while the simulation was running, if available.
  @Input() movementBoxes: ISimulationMovementBox[] = null;

  @Input() isOpen: boolean = false;

  scene$: Observable<any>;

  ngOnChanges$ = new ReplaySubject<SimpleChanges>(1);

  robotModelForDownload: URDFRobot;
  robotModel: URDFRobot;
  conveyorModel: THREE.Object3D;
  wrist3Model: THREE.Object3D;

  // Boxes in the world, NOTE! NOT GRIPPED
  boxTransforms = new Map<string, Transform>();
  boxes = new InstancedDynamicBox(
    new THREE.Mesh(
      new THREE.BoxGeometry(0, 0, 0),
      new THREE.MeshLambertMaterial()
    ),
    25000 // Max number of possible boxes at any one time
  );

  // Gripped boxes
  grippedBoxTransforms = new Map<string, Transform>();
  grippedBoxes = new InstancedDynamicBox(
    new THREE.Mesh(
      new THREE.BoxGeometry(0, 0, 0),
      new THREE.MeshLambertMaterial()
    ),
    8 // Max possible grip in simulator is 8
  );
  prevGripperState = false;

  dummy: THREE.Object3D = new THREE.Object3D();

  customerOrgLogoPlate: THREE.Mesh;
  salesOrgLogoPlate: THREE.Mesh;

  /**
   * Debug mode is used to simulate a 30fps animation cycle.
   * This is used to test the player independent of how much time has
   * actually passed between frames (as debug stepping is far far longer
   * than runtime frames).
   * **NOTE! It should be turned off in production**.
   */
  debugMode = false;

  constructor(
    ngZone: NgZone,
    private assetStoreSerivce: NewAssetStoreService,
    public rosbagPlayer: RosbagPlayerService,
    public notification: NotificationService,
    renderingService: RenderingService,
    cdRef: ChangeDetectorRef,
    public threeExporterService: ThreeExporterService
  ) {
    super(ngZone, renderingService, cdRef);

    // Syncronize debug mode between player service and view.
    this.rosbagPlayer.setDebugMode(this.debugMode);

    this.boxes.name = 'BOXES';
    this.boxes.setUsage(THREE.DynamicDrawUsage);
    this.boxes.visible = false;
    this.scene.add(this.boxes);
    this.grippedBoxes.name = 'GRIPPED_BOXES';
    this.grippedBoxes.setUsage(THREE.DynamicDrawUsage);
    this.grippedBoxes.visible = false;
    this.renderingOptions = {
      shadowMap: {
        enabled: true,
        type: THREE.VSMShadowMap,
      },
    };

    this.scene$ = this.ngOnChanges$.pipe(
      switchMap(() => {
        return combineLatest([
          this.assetStoreSerivce.load(
            'sim_robot_' + this.id,
            this.urdfURL,
            'urdf',
            true
          ).data$,
          this.assetStoreSerivce.load('simulation-scene').data$,
        ]);
      }),
      map(([m, scene]: [URDFRobot, THREE.Group]) => {
        this.prepRobotmodelForDownload(m);
        scene.rotation.x = MathUtils.degToRad(90);
        // Take note of the logo plate, need it for
        // assigning logos once those are ready.
        this.customerOrgLogoPlate = scene.getObjectByName(
          'Logo_1'
        ) as THREE.Mesh;
        this.salesOrgLogoPlate = scene.getObjectByName('Logo_2') as THREE.Mesh;
        (this.salesOrgLogoPlate.material as THREE.MeshPhongMaterial).alphaMap =
          null;

        if (this.robotModel) {
          this.scene.remove(this.robotModel);
          ThreeUtils.disposeObject(this.robotModel);
        }

        this.robotModel = m;
        this.robotModel.rotation.x = MathUtils.degToRad(-90);

        this.robotModel.traverse((child) => {
          switch (child.name) {
            case 'world_joint_conveyor': {
              this.conveyorModel = child;
              break;
            }
            case 'wrist_3_link': {
              this.wrist3Model = child;
              this.wrist3Model.add(this.grippedBoxes);
              break;
            }
            case 'world_pally_joint': {
              const visual = URDFUtils.findVisualFromJoint(child as URDFJoint);
              visual.position.set(0, 0, 0);
              for (const vChild of visual.children) {
                visual.remove(vChild);
              }
              visual.add(scene);
              break;
            }
          }
        });

        // Set to default pose
        for (const jointname of Object.keys(defaultRobotPose)) {
          URDFUtils.setJointAngle(
            this.robotModel.joints[jointname],
            MathUtils.degToRad(defaultRobotPose[jointname])
          );
        }

        for (const jointname of Object.keys(this.robotModel.joints)) {
          if (jointname.includes('column')) {
            this.robotModel.joints[jointname].ignoreLimits = true;
          }
        }

        this.scene.add(this.robotModel);

        this.render();
        return m;
      }),
      map(() => {
        // Check that the model is filled and throw
        // error if not (parts probably failed loading).
        let nrOfVisuals = 0;
        let nrFilledVisuals = 0;
        this.robotModel.traverse((child: any) => {
          if (!child.isURDFVisual) {
            return;
          }

          nrOfVisuals++;
          if (child.children.length !== 0) {
            nrFilledVisuals++;
          }
        });

        const isNotFullyFilled = nrFilledVisuals / nrOfVisuals < 0.5;
        if (isNotFullyFilled) {
          const msg =
            'Robot fails to load without error. Please try to refresh. Contact MRC support if this error persists.';
          this.notification.showError(msg);
          throw new Error(msg);
        }
      }),
      switchMap(() => this.productModelId$),
      skipWhile((modelId) => !modelId),
      take(1),
      switchMap((modelId) =>
        combineLatest([this.assetStoreSerivce.load(modelId).data$, of(modelId)])
      ),
      map(([asset, _modelId]) => {
        // Extract the actual mesh.
        // Initially this mesh is rotated inside the asset group.
        // Which switches the x/z axis, which is hard to understand.
        const model = asset.children[0] as THREE.Mesh;
        ThreeUtils.disposeObject(this.boxes.boxMesh);
        ThreeUtils.disposeObject(this.grippedBoxes.boxMesh);

        this.boxes.boxMesh.geometry = model.geometry;
        this.boxes.boxMesh.material = model.material;
        this.grippedBoxes.boxMesh.geometry = model.geometry;
        this.grippedBoxes.boxMesh.material = model.material;
      }),
      // Hold back the pipeline til ngOnInit has run.
      // NOTE! Ensures camera and controls are ready.
      switchMap(() => this.ngOnInit$),
      map(() => {
        this.centerControlsToRobot();

        this.render();

        // View is no longer loading.
        return false;
      }),
      // Mitigates re-adding of models to the scene
      shareReplay({ bufferSize: 1, refCount: false }),
      takeUntil(this.destroy$)
    );

    this.loading$ = this.scene$.pipe(
      take(1),
      startWith(true),
      toRequestState(),
      takeUntil(this.destroy$)
    );

    // Add lighting
    this.addLights();

    this.ngOnChanges$
      .pipe(
        filter((changes: SimpleChanges) =>
          Boolean(changes.movementBoxes?.currentValue)
        ),
        map((changes: SimpleChanges) => {
          const added = changes.movementBoxes.currentValue.filter(
            (item) =>
              !changes.movementBoxes.previousValue?.some(
                (other) => item?.id === other?.id
              )
          );
          const removed = changes.movementBoxes.previousValue?.filter(
            (item) =>
              !changes.movementBoxes.currentValue.some(
                (other) => item?.id === other?.id
              )
          );

          // Remove from scene all the boxes that are no longer in the movementBoxes array.
          removed?.forEach((box: ISimulationMovementBox) => {
            this.scene.children
              .filter((child) => child.name === box.id)
              .forEach((box) => this.scene.remove(box));
          });

          // Add to scene all the boxes that are in the movementBoxes array but not in the scene.
          added?.forEach((box: ISimulationMovementBox) => {
            if (box) {
              const singleThreeMovementBox = new THREE.Box3();
              const singleThreeMovementBoxHelper = new THREE.Box3Helper(
                singleThreeMovementBox
              );
              /*
                This section is kinda hard to wrap your head around.
                1. Coordinate System Discrepancy:
                  - Simulator uses a Z-up coordinate system (common in robotics).
                  - Three.js uses a Y-up coordinate system (common in graphics).
                  - Due to this difference, assets from ROS appear rotated by 90°
                  around the X-axis in Three.js.
                2. Size Calculation:
                  - We've adjusted for the Y-Z axis swap here so that when
                  determining the center point later,
                  we don't need to think about the axis swap.
                  - Effectively, `size.y` represents depth (z-axis in ROS) and
                  `size.z` represents height (y-axis in ROS).
                3. Center Calculation:
                  - To find the center, we first start from the minimum corner of
                  the box (the base reference point) and swap the z and y, like we
                  did in the size calculation.
                  - We then move by half the size in each dimension to find
                  the exact center of the box.
                  - The Z-axis direction is also inverted due to the difference
                  in how Z is oriented in ROS vs. Three.js.
                */
              const size = new THREE.Vector3(
                box.x_max - box.x_min,
                box.z_max - box.z_min,
                box.y_max - box.y_min
              );
              const center = new THREE.Vector3(
                box.x_min + size.x / 2,
                box.z_min + size.y / 2, // Y axis is inverted to Z.
                -(box.y_min + size.z / 2) // Z axis is inverted to Y
              );
              singleThreeMovementBox.makeEmpty();
              singleThreeMovementBox.setFromCenterAndSize(center, size);
              singleThreeMovementBoxHelper.updateMatrixWorld(true);
              singleThreeMovementBoxHelper.material['color'].setHex(
                parseInt('' + box.guiColor)
              );
              singleThreeMovementBoxHelper.visible = true;
              singleThreeMovementBoxHelper.name = box.id;
              this.scene.add(singleThreeMovementBoxHelper);
            }
          });
          this.render();
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();

    this.ngOnChanges$
      .pipe(
        filter((changes: SimpleChanges) => Boolean(changes.isOpen)),
        map((changes: SimpleChanges) => changes.isOpen.currentValue)
      )
      .subscribe((isOpen: boolean) => {
        if (!isOpen) {
          // Collsion topic
          this.subToMessages('/robot_contact', this.onCollision);
        }
      });
  }

  applyCompanyLogoOnScene(logo: THREE.Texture, plate: THREE.Mesh): void {
    const size = new THREE.Box3()
      .expandByObject(plate)
      .getSize(new THREE.Vector3());

    // Calc the aspect ratio for the plane the image will be mapped to.
    let planeRatio = size.x / size.y;

    (plate.material as THREE.MeshPhongMaterial).map =
      ThreeUtils.prepLogoForDisplay(logo, planeRatio, '#fafafa');
    (plate.material as THREE.MeshPhongMaterial).alphaMap = null;

    /**
     * Every point on a model can be mapped to a specific
     * position on a texture called a "UV" coordinate. These
     * coords goes from (0,0) -> (1,0), where "(0,0)" is the lower left and "(1,1)"
     * represents upper right of the texture.
     * UV coords are pixel independent.
     *
     * It's possible to map outside the (0,0) -> (1,1) range, but depending on
     * the settings of the material on the model, you might get
     * differing and often unwanted results. If so, use a different
     * wrapping method on the material and experiment.
     *
     * In this case we get the positions of every vertex that
     * makes up the geomerty and scale it to a 1x1 size. This
     * makes it compatible for use in texture space (UV, (0,0) -> (1,1)).
     * After this we shift all points by half of the texture. If we
     * don't do this we'll only show the lower left quarter of the
     * texture and the rest will be some kind of unwanted result.
     *
     * You can use "ThreeUtils.visualizeUVsOnTexture(texture, geometry)"
     * and add it to the scene to visualize how the uv's are mapped
     * to the texture.
     */
    const vertecies = ThreeUtils.getVertecies(plate.geometry).map(
      (v: THREE.Vector3) => v.divide(size).addScalar(0.5)
    );

    /**
     * Make a new buffer attribute to replace the current uvs.
     * Have to do this as buffer attributes are immutable / unchangable.
     */
    const uvs = new Float32Array(vertecies.length * 2);
    for (let i = 0; i < vertecies.length; i++) {
      uvs[i * 2] = vertecies[i].x;
      uvs[i * 2 + 1] = vertecies[i].y;
    }
    plate.geometry.attributes.uv = new THREE.BufferAttribute(uvs, 2);
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.camera.position.copy(settings.view3d.simvisCameraPosition);

    // Don't let the camera outside the background cylinder
    this.controls.maxDistance = 9;
    this.controls.update();

    // Subscribe to player specific events
    this.subToPlayerEvent('play', this.onPlayerPlay);
    this.subToPlayerEvent('pause', this.onPlayerPause);
    this.subToPlayerEvent('pre-sliderseek', this.onPlayerSeek);
    this.subToPlayerEvent('post-sliderseek', this.render.bind(this));
    this.subToPlayerEvent('playafterstop', this.onPlayerPlayAfterStop);

    // Robot and lifting column joint states
    this.subToMessages('/playback/joint_states', this.onJointState);

    // All boxes in motion except (not including palleted boxes)
    this.subToMessages('/world/boxes', this.onWorldBoxState);

    // All boxes on the pallets
    this.subToMessages('/world/pallet/boxes', this.onWorldBoxState);

    // Gripper activity state and boxes gripped
    this.subToMessages('/world/gripper', this.onWorldGripperState);
  }

  private subToPlayerEvent(
    event: RosbagPlayerEventType,
    func: (data: any) => void
  ): Subscription {
    return iif(
      () => event === '*',
      this.rosbagPlayer.playerEvents$,
      this.rosbagPlayer.playerEvents$.pipe(filter((e) => e.type === event))
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe(func);
  }

  private subToMessages(
    topic: string,
    func: (msg: ReadResult) => void
  ): Subscription {
    return iif(
      () => topic === '*',
      this.rosbagPlayer.messages$,
      this.rosbagPlayer.messages$.pipe(filter((msg) => msg.topic === topic))
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe(func);
  }

  override ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);

    this.ngOnChanges$.next(changes);
  }

  prepRobotmodelForDownload(model: THREE.Object3D): void {
    // Make a copy.
    this.robotModelForDownload = URDFUtils.clone(model);

    // Remove the scene is it exists.
    const sceneModel = this.robotModelForDownload.children.find(
      (c: THREE.Object3D) => c.name === 'world_pally_joint'
    );
    if (sceneModel) {
      sceneModel.removeFromParent();
    }

    // Set to default pose.
    for (const jointname of Object.keys(defaultRobotPose)) {
      URDFUtils.setJointAngle(
        this.robotModelForDownload.joints[jointname],
        MathUtils.degToRad(defaultRobotPose[jointname])
      );
    }
    this.robotModelForDownload.updateMatrixWorld();
  }

  downloadRobotmodel(filename?: string): void {
    this.threeExporterService.export(
      this.robotModelForDownload,
      'stl',
      filename
    );
  }

  ngAfterViewInit(): void {
    this.ngOnChanges$
      .pipe(
        filter((changes: SimpleChanges) => Boolean(changes.boxSticker)),
        switchMap((changes: SimpleChanges) =>
          combineLatest([of(changes), this.scene$])
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(([changes, _]) => {
        if (
          (changes.boxSticker || changes.labelOrientation) &&
          changes.boxSticker.currentValue &&
          this.labelOrientation !== LabelOrientation.NULL &&
          this.labelOrientation !== undefined
        ) {
          this.boxes.setLabels(changes.boxSticker.currentValue, [
            this.labelOrientation,
          ]);
          this.grippedBoxes.setLabels(changes.boxSticker.currentValue, [
            this.labelOrientation,
          ]);
        }

        this.render();
      });
    this.ngOnChanges$
      .pipe(
        takeUntil(this.destroy$),
        filter((changes: SimpleChanges) =>
          Boolean(changes.customerOrgWallSticker || changes.salesOrgWallSticker)
        ),
        switchMap((changes: SimpleChanges) =>
          combineLatest([of(changes), this.scene$])
        )
      )
      .subscribe(([changes, _]) => {
        if (changes.customerOrgWallSticker?.currentValue) {
          this.applyCompanyLogoOnScene(
            this.customerOrgWallSticker,
            this.customerOrgLogoPlate
          );
        }

        if (changes.salesOrgWallSticker?.currentValue) {
          this.applyCompanyLogoOnScene(
            this.salesOrgWallSticker,
            this.salesOrgLogoPlate
          );
        }

        this.render();
      });
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();

    ThreeUtils.disposeObject(this.robotModelForDownload);
  }

  centerControlsToRobot(): void {
    const robotPos = new THREE.Vector3();
    this.robotModel.joints['bracket_base_link_joint'].getWorldPosition(
      robotPos
    );

    this.camera.position.copy(settings.view3d.simvisCameraPosition);
    this.controls.target.copy(robotPos);
    this.controls.update();
  }

  onPlayerPlay = (_data: any) => {
    this.enableRenderingLoop();
  };

  onPlayerPause = (_data: any) => {
    this.enableRenderingLoop(false);
  };

  onPlayerPlayAfterStop = (_data: any) => {
    this.prevGripperState = false;
    this.boxes.boxMesh.count = 0;

    this.boxTransforms.clear();
    this.updateBoxes(this.boxes, this.boxTransforms);

    this.grippedBoxTransforms.clear();
    this.updateBoxes(this.grippedBoxes, this.grippedBoxTransforms);

    this.render();
  };

  onPlayerSeek = (_data: any) => {
    this.prevGripperState = false;
    this.boxes.boxMesh.count = 0;

    this.boxTransforms.clear();
    this.updateBoxes(this.boxes, this.boxTransforms);

    this.grippedBoxTransforms.clear();
    this.updateBoxes(this.grippedBoxes, this.grippedBoxTransforms);
  };

  onJointState = (msg: ReadResult) => {
    for (let index = 0; index < msg.message.name.length; index++) {
      const jointname = msg.message.name[index];
      const jointangle = msg.message.position[index];
      if (jointname in this.robotModel.joints) {
        URDFUtils.setJointAngle(this.robotModel.joints[jointname], jointangle);
      }
    }

    this.updateBoxes(this.boxes, this.boxTransforms);
  };

  onWorldBoxState = (msg: ReadResult) => {
    for (let idIndex = 0; idIndex < msg.message.box_ids.length; idIndex++) {
      const boxID = msg.message.box_ids[idIndex];
      if (!boxID.includes('box')) {
        // Skip anything thats isn't boxes
        continue;
      }

      // Box not on gripper
      if (!this.grippedBoxTransforms.get(boxID)) {
        const boxData = msg.message.boxes[idIndex];

        // Try to find box with given id
        let box = this.boxTransforms.get(boxID);

        // Couldn't find box, make it
        if (!box) {
          box = new Transform({
            scale: new THREE.Vector3(
              boxData.dimension[0],
              boxData.dimension[2],
              boxData.dimension[1]
            ),
          });
          this.boxTransforms.set(boxID, box);
        }

        // Place and rotate box
        box.position.set(
          boxData.position.x,
          boxData.position.z, // z <-> y Axiz swapped between sim and three.js
          -boxData.position.y // Y-axis is flipped between sim and three.js
        );
        box.rotation.set(
          boxData.orientation.x,
          boxData.orientation.z, // z <-> y Axiz swapped between sim and three.js
          -boxData.orientation.y, // Y-axis is flipped between sim and three.js
          boxData.orientation.w
        );
      }
    }

    this.updateBoxes(this.boxes, this.boxTransforms);
  };

  onWorldGripperState = (msg: ReadResult) => {
    // Gripper activation/box pickup
    if (msg.message.enabled && !this.prevGripperState) {
      for (
        let idIndex = 0;
        idIndex < msg.message.attached_boxes.length;
        idIndex++
      ) {
        const boxID = msg.message.attached_boxes[idIndex];
        if (!boxID.includes('box')) {
          // Skip anything thats isn't boxes
          continue;
        }

        const box = this.boxTransforms.get(boxID);
        this.boxTransforms.delete(boxID);
        if (Boolean(box)) {
          // Place and rotate box under gripper
          this.dummy.position.set(
            msg.message.position[idIndex].x,
            msg.message.position[idIndex].y,
            msg.message.position[idIndex].z
          );
          // Use dummy object to rotate box's quaternion 90 degreen on x axis
          this.dummy.setRotationFromQuaternion(
            new THREE.Quaternion(
              msg.message.orientation[idIndex].x,
              msg.message.orientation[idIndex].y,
              msg.message.orientation[idIndex].z,
              msg.message.orientation[idIndex].w
            )
          );
          this.dummy.rotateX(Math.PI / 2); // Rotate 90 degrees
          box.position.copy(this.dummy.position);
          box.rotation.setFromEuler(this.dummy.rotation);

          // Grip box
          this.grippedBoxTransforms.set(boxID, box);
        }
      }

      this.grippedBoxes.visible = true;
    }

    // Gripper deactivated
    if (!msg.message.enabled && this.prevGripperState) {
      // Get all gripped boxes
      for (const [id, box] of this.grippedBoxTransforms) {
        this.boxTransforms.set(id, box);
      }
      this.grippedBoxTransforms.clear();
      this.grippedBoxes.visible = false;
    }

    this.prevGripperState = msg.message.enabled as boolean;
    this.updateBoxes(this.grippedBoxes, this.grippedBoxTransforms);
  };

  onCollision = (msg: ReadResult) => {
    const collisionIndicator = new NewCollsionIndicator(3000);
    collisionIndicator.position.set(
      msg.message.position.x,
      msg.message.position.z,
      -msg.message.position.y // Y-axis is flipped in simulator
    );
    this.scene.add(collisionIndicator);
    collisionIndicator.changes$
      .pipe(
        filter((v) => v == 'deactivate'),
        take(1)
      )
      .subscribe(() => {
        ThreeUtils.disposeObject(collisionIndicator);
      });
    collisionIndicator.activate(5000);

    return;
  };

  updateBoxes(
    boxesObj: InstancedDynamicBox,
    transforms: Map<string, Transform>
  ): void {
    let i = 0;
    boxesObj.setCount(transforms.size);
    for (const [_, box] of transforms) {
      boxesObj.setMatrixAt(i, box.asMatrix());
      i++;
    }
    boxesObj.visible = transforms.size > 0;
  }

  private addLights(): void {
    const ambient = new THREE.AmbientLight('#fafafa', 0.4);
    this.scene.add(ambient);

    const pointLight = new THREE.PointLight('#fafafa', 0.4, 20, 1);
    pointLight.position.set(0, 10, 0);
    this.scene.add(pointLight);

    const directionalLight = new THREE.DirectionalLight('#fafafa', 0.15);
    directionalLight.position.set(0, 10, 0);
    this.scene.add(directionalLight);

    const spotLight = new THREE.SpotLight('#fafafa');
    spotLight.position.set(0, 7, -7);
    spotLight.angle = Math.PI / 5;
    spotLight.intensity = 0.05;
    spotLight.decay = 1;
    spotLight.penumbra = 0.25;
    spotLight.distance = 14;
    this.scene.add(spotLight);
    spotLight.target.position.set(0, 0, -13);
    this.scene.add(spotLight.target);
    spotLight.updateMatrixWorld(true);
    spotLight.target.updateMatrixWorld(true);

    const spotLight2 = new THREE.SpotLight('#fafafa');
    spotLight2.position.set(5, 0.5, 0);
    spotLight2.angle = Math.PI / 7;
    spotLight2.intensity = 0.25;
    spotLight2.decay = 0.1;
    spotLight2.penumbra = 0.5;
    spotLight2.distance = 7;
    this.scene.add(spotLight2);
    spotLight2.target.position.set(0, 1.5, 0);
    spotLight2.target.updateMatrixWorld(true);
    this.scene.add(spotLight2.target);

    const spotLight3 = new THREE.SpotLight('#fafafa');
    spotLight3.position.set(-5, 0.5, 0);
    spotLight3.angle = Math.PI / 7;
    spotLight3.intensity = 0.25;
    spotLight3.decay = 0.1;
    spotLight3.penumbra = 0.5;
    spotLight3.distance = 7;
    this.scene.add(spotLight3);
    spotLight3.target.position.set(0, 1.5, 0);
    spotLight3.target.updateMatrixWorld(true);
    this.scene.add(spotLight3.target);
  }
}
